import { useSearchParams } from 'react-router-dom';
import StaffPageShell from '../components/StaffPageShell';
import MessageDisplay from '../components/MessageDisplay';
import ModuleCompletionMatrix from '../components/ModuleCompletion/ModuleCompletionMatrix';
import SearchableSelect from '../components/Form/SearchableSelect';
import { BATCH_SEARCHABLE_SELECT_PROPS } from '../domain/batch/batchDisplayLabel';
import useModuleCompletionMatrix from '../hooks/useModuleCompletionMatrix';
import '../styles/module-completion.css';

/**
 * Module completion report + status updates for a batch (trainer / staff).
 */
export default function ModuleCompletionPage() {
  const [searchParams] = useSearchParams();
  const initialBatchId = searchParams.get('batchId') || '';

  const {
    batches,
    selectedBatchId,
    setSelectedBatchId,
    matrix,
    loadingBatches,
    loadingMatrix,
    savingKey,
    error,
    success,
    updateStatus,
  } = useModuleCompletionMatrix(initialBatchId);

  return (
    <StaffPageShell
      title="Module completion"
      subtitle="Report and update Completed / Ongoing per student module"
      wide
    >
      <p className="staff-page-lead">
        Choose a batch to see each student’s progress across the course modules. When every module
        is marked Completed, interview readiness is updated to “all modules complete”.
      </p>

      <MessageDisplay error={error} success={success} />

      <div className="module-completion-toolbar">
        <label className="module-completion-batch-label" htmlFor="module-completion-batch">
          Batch
        </label>
        <div className="module-completion-batch-select" style={{ minWidth: '16rem' }}>
          <SearchableSelect
            id="module-completion-batch"
            value={selectedBatchId}
            disabled={loadingBatches || batches.length === 0}
            onChange={(e) => setSelectedBatchId(e.target.value)}
            placeholder="Select a batch…"
            options={batches}
            {...BATCH_SEARCHABLE_SELECT_PROPS}
          />
        </div>
      </div>

      {loadingBatches ? (
        <p className="staff-page-lead">Loading batches…</p>
      ) : batches.length === 0 ? (
        <p className="staff-page-lead">
          No batches available. Trainers only see batches linked under Batch management → Manage
          trainers.
        </p>
      ) : loadingMatrix && selectedBatchId ? (
        <p className="staff-page-lead">Loading module completion…</p>
      ) : selectedBatchId && matrix ? (
        <>
          <p className="staff-page-lead" style={{ marginBottom: '0.75rem' }}>
            {matrix.batch?.batchName}
            {matrix.batch?.courseName ? ` · ${matrix.batch.courseName}` : ''}
            {` · ${matrix.summary?.totalModules ?? 0} modules · ${
              matrix.summary?.totalStudents ?? 0
            } students`}
          </p>
          <ModuleCompletionMatrix
            matrix={matrix}
            savingKey={savingKey}
            onStatusChange={updateStatus}
          />
        </>
      ) : selectedBatchId ? null : (
        <p className="staff-page-lead">Select a batch to open the completion matrix.</p>
      )}
    </StaffPageShell>
  );
}
